import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Shield, Calendar, CheckCircle, Globe, LogIn, MapPin, Search, Vote, ChevronRight, Zap, Lock, Bell, Navigation, Users, Clock, Compass } from 'lucide-react';
import { collectionGroup, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAppContext } from '../../context/AppContext';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import CityAutocomplete from '../../components/ui/CityAutocomplete';
import PositionBadge from '../../components/ui/PositionBadge';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import { parseLocalDate, displayName } from '../../utils/calculations';
import { haversineDistance, geocodeCity, normalizeCity } from '../../utils/geo';
import logger from '../../utils/logger';

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const WEEK_DAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DAY_FULL = { seg: 'segunda-feira', ter: 'terça-feira', qua: 'quarta-feira', qui: 'quinta-feira', sex: 'sexta-feira', sab: 'sábado', dom: 'domingo' };

const formatGameDate = (date, time) => {
  if (!date) return time || null;
  const [year, month, day] = date.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const label = `${WEEK_DAYS_SHORT[d.getDay()]}, ${day} de ${MONTHS[month - 1]}`;
  return time ? `${label} às ${time}` : label;
};

const formatRecurrence = (dayOfWeek, defaultTime) => {
  const day = DAY_FULL[dayOfWeek];
  if (!day) return defaultTime ? `Às ${defaultTime}` : null;
  return defaultTime ? `Toda ${day} às ${defaultTime}` : `Toda ${day}`;
};

const HomePage = () => {
  const { groups, publicGroups, allGames, userId, users, profile, fetchUserProfile, requestToJoin, checkinOpenSpot, invites, respondInvite } = useAppContext();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('homeTab') || 'meus'); // 'meus' | 'descobrir'
  const [radius, setRadius] = useState(50);
  // debouncedRadius: valor usado nos cálculos/filtros — só atualiza 400ms após o slider parar
  const [debouncedRadius, setDebouncedRadius] = useState(50);
  const [hideBanner, setHideBanner] = useState(localStorage.getItem('hideAvailBanner') === 'true');
  // Override de cidade: permite o user mudar cidade temporariamente sem editar o perfil
  const [overrideCity, setOverrideCity] = useState('');
  const [pendingCity, setPendingCity] = useState(''); // o que está a ser digitado no CityAutocomplete
  const [overrideCoords, setOverrideCoords] = useState(null);
  const [peladeiros, setPeladeiros] = useState([]);
  const [editingCity, setEditingCity] = useState(false);
  const geocodedRef = useRef({});
  const cityGeoCache = useRef({});
  const peladeirosCache = useRef(new Map()); // cache: cidade normalizada → array de peladeiros

  // Debounce do radius: evita recalcular filtros a cada pixel do slider
  useEffect(() => {
    const t = setTimeout(() => setDebouncedRadius(radius), 400);
    return () => clearTimeout(t);
  }, [radius]);

  // Persistir aba activa
  useEffect(() => { localStorage.setItem('homeTab', activeTab); }, [activeTab]);

  // Cidade do perfil como string estável (evita deps de objeto que muda referência)
  const profileCity = profile?.city ?? '';

  // Pre-carregar avatares dos jogadores confirmados
  useEffect(() => {
    const loadPlayers = async () => {
      const ids = new Set();
      allGames.forEach(g => g.confirmedIds?.forEach(id => ids.add(id)));
      for (const id of ids) {
        if (!users[id] && !id.startsWith('guest_')) await fetchUserProfile(id);
      }
    };
    if (allGames.length > 0) loadPlayers();
  }, [allGames, users, fetchUserProfile]);

  // Geocodificar grupos sem lat/lng mas com cidade (uma vez por sessão, só estado local)
  useEffect(() => {
    const toGeocode = publicGroups.filter(g => g.city && !g.lat && geocodedRef.current[g.id] !== true && !geocodedRef.current[g.id]?.lat);
    if (toGeocode.length === 0) return;
    toGeocode.forEach(async (g) => {
      geocodedRef.current[g.id] = true; // marcar para não repetir
      const coords = await geocodeCity(g.city);
      if (coords) {
        geocodedRef.current[g.id] = coords;
        logger.log('Geocoded group', g.id, g.city, coords);
      }
    });
  }, [publicGroups]);

  // Geocodificar override de cidade quando o utilizador escolhe uma nova cidade
  useEffect(() => {
    if (!overrideCity) {
      setOverrideCoords(null);
      return;
    }
    let cancelled = false;
    geocodeCity(overrideCity).then(coords => {
      if (!cancelled) setOverrideCoords(coords || null);
    }).catch(err => {
      logger.error('Geocoding overrideCity falhou:', err);
    });
    return () => { cancelled = true; };
  }, [overrideCity]);

  // Buscar peladeiros do Firestore quando a cidade activa muda — com cache por cidade
  useEffect(() => {
    const activeCity = overrideCity || profileCity;
    if (!activeCity) { setPeladeiros([]); return; }

    const key = normalizeCity(activeCity);

    // Cache hit: sem query ao Firestore
    if (peladeirosCache.current.has(key)) {
      setPeladeiros(peladeirosCache.current.get(key));
      return;
    }

    getDocs(collectionGroup(db, 'profile'))
      .then(snap => {
        const data = snap.docs
          .filter(d => d.id === 'data' && d.data().name && d.data().availability)
          .map(d => ({ id: d.ref.parent.parent.id, ...d.data() }));
        peladeirosCache.current.set(key, data);
        setPeladeiros(data);
        // Geocodificar cidades de peladeiros sem lat/lng
        const uniqueCities = [...new Set(data.filter(u => !u.lat && !u.lng && u.city).map(u => u.city))];
        uniqueCities.forEach(city => {
          if (cityGeoCache.current[city] !== undefined) return;
          cityGeoCache.current[city] = null;
          geocodeCity(city).then(coords => {
            cityGeoCache.current[city] = coords;
            if (coords) {
              const updated = data.map(u =>
                u.city === city && !u.lat && !u.lng ? { ...u, lat: coords.lat, lng: coords.lng } : u
              );
              peladeirosCache.current.set(key, updated);
              setPeladeiros(prev => prev.map(u =>
                u.city === city && !u.lat && !u.lng ? { ...u, lat: coords.lat, lng: coords.lng } : u
              ));
            }
          }).catch(() => {});
        });
      })
      .catch(err => logger.error('Erro ao carregar peladeiros:', err));
  }, [overrideCity, profileCity]);

  const getActiveGame = (group) => {
    const groupGames = allGames.filter(g => g.groupId === group.id && (g.status === 'scheduled' || g.status === 'voting'));
    return groupGames.sort((a, b) => {
      if (a.status === 'voting' && b.status !== 'voting') return -1;
      if (b.status === 'voting' && a.status !== 'voting') return 1;
      return parseLocalDate(a.date) - parseLocalDate(b.date);
    })[0];
  };

  const getUser = (uid) => users[uid] || { photo: '👤', name: '...' };

  // Cidade activa: override ou perfil
  const activeCity = overrideCity || profile?.city || '';
  // Coords activas: override ou perfil (lat/lng garantidos pela migração no AppContext)
  const activeLat = overrideCoords?.lat ?? profile?.lat;
  const activeLng = overrideCoords?.lng ?? profile?.lng;
  const hasProfileLocation = !!(activeLat && activeLng);

  // Filtrar grupos por raio automático (usando cidade activa)
  // isMemberList: se true, não aplica filtro de raio (meus grupos sempre visíveis)
  const filterGroups = (list, isMemberList = false) => {
    return list.filter(g => {
      // Filtro por nome (aplica a todas as secções)
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!g.name?.toLowerCase().includes(q) && !g.courtName?.toLowerCase().includes(q)) return false;
      }
      // Filtro por raio só para discovery (não para meus grupos)
      if (!isMemberList && hasProfileLocation) {
        const gLat = g.lat || (geocodedRef.current[g.id]?.lat);
        const gLng = g.lng || (geocodedRef.current[g.id]?.lng);
        if (gLat && gLng) {
          const dist = haversineDistance(activeLat, activeLng, gLat, gLng);
          if (dist > debouncedRadius) return false;
        } else {
          // grupo sem coords: fica fora do feed automático de discovery
          return false;
        }
      }
      return true;
    });
  };

  // Meus grupos: sempre visíveis (sem filtro de raio)
  const filteredGroups = filterGroups(groups, true);
  // Grupos públicos: filtrados por raio/cidade para discovery
  const filteredPublicGroups = filterGroups(publicGroups, false);

  // Calcular vagas de um grupo
  const getSpotsInfo = (group) => {
    const nextGame = getActiveGame(group);
    if (!nextGame) return null;
    const confirmed = nextGame.confirmedIds?.length || 0;
    const max = nextGame.maxPlayers || 14;
    const spots = Math.max(0, max - confirmed);
    return { spots, max, confirmed, game: nextGame };
  };

  // Jogos com vagas abertas (inclui privados com openSpots)
  const openSpotGames = useMemo(() => {
    return allGames
      .filter(g => {
        if (g.status !== 'scheduled') return false;
        if (!g.openSpots || g.openSpots <= 0) return false;
        const taken = g.externalCheckins?.length || 0;
        if (taken >= g.openSpots) return false;
        const isMember = groups.some(gr => gr.id === g.groupId);
        if (isMember) return false;
        if (g.externalCheckins?.includes(userId)) return false;
        const group = [...groups, ...publicGroups].find(gr => gr.id === g.groupId);
        if (!group) return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          if (!group.name?.toLowerCase().includes(q) && !group.courtName?.toLowerCase().includes(q)) return false;
        }
        return true;
      })
      .map(g => ({
        ...g,
        group: [...groups, ...publicGroups].find(gr => gr.id === g.groupId),
        spotsLeft: g.openSpots - (g.externalCheckins?.length || 0),
      }));
  }, [allGames, groups, publicGroups, userId, searchQuery]);

  // Jogos de checkin externo do user (aguardando ou confirmados)
  const externalCheckinGames = useMemo(() => {
    return allGames.filter(g => {
      if (g.status !== 'scheduled') return false;
      const isMember = groups.some(gr => gr.id === g.groupId);
      if (isMember) return false;
      const isPending = (g.externalCheckins || []).includes(userId);
      const isConfirmed = (g.confirmedExternalCheckins || []).includes(userId);
      return isPending || isConfirmed;
    }).map(g => ({
      ...g,
      group: [...groups, ...publicGroups].find(gr => gr.id === g.groupId),
    }));
  }, [allGames, groups, publicGroups, userId]);

  // Check-ins externos pendentes em jogos do user como admin
  const pendingExternalCheckinGames = useMemo(() => {
    const adminGroupIds = new Set(groups.filter(g => g.ownerId === userId || (g.adminIds || []).includes(userId)).map(g => g.id));
    return allGames.filter(g => {
      if (!adminGroupIds.has(g.groupId)) return false;
      if (g.status !== 'scheduled') return false;
      const pending = (g.externalCheckins || []).filter(id => !(g.confirmedExternalCheckins || []).includes(id));
      return pending.length > 0;
    });
  }, [allGames, groups, userId]);

  // Verificar se o utilizador tem disponibilidade activa
  const hasAvailability = useMemo(() => {
    if (!profile?.availability) return false;
    return Object.values(profile.availability).some(v => v !== null);
  }, [profile]);

  // Peladeiros disponíveis na cidade activa
  const availablePlayers = useMemo(() => {
    if (!activeCity || peladeiros.length === 0) return [];

    const activeCoords = overrideCoords
      || (profile?.lat && profile?.lng ? { lat: profile.lat, lng: profile.lng } : null);

    return peladeiros
      .filter(u => {
        if (!u.city) return false;
        if (activeCoords && u.lat && u.lng) {
          const dist = haversineDistance(activeCoords.lat, activeCoords.lng, u.lat, u.lng);
          if (dist > debouncedRadius) return false;
        } else {
          if (normalizeCity(u.city) !== normalizeCity(activeCity)) return false;
        }
        // Deve ter pelo menos um dia activo de disponibilidade
        const hasActiveDays = Object.values(u.availability || {})
          .some(v => v !== null && (Array.isArray(v) ? v.length > 0 : true));
        return hasActiveDays;
      })
      .slice(0, 10);
  }, [peladeiros, profile, activeCity, overrideCoords, debouncedRadius]);

  const hasDiscovery = filteredPublicGroups.length > 0 || openSpotGames.length > 0 || availablePlayers.length > 0;
  const discoveryCount = filteredPublicGroups.length + openSpotGames.length;

  // Notificações que requerem atenção do user na aba "Meus Grupos"
  const meusNotificationCount =
    invites.length +
    (groups.filter(g => g.ownerId === userId).reduce((s, g) => s + (g.pendingRequests?.length || 0), 0)) +
    pendingExternalCheckinGames.length;

  return (
    <>
      <Header />
      <div className="p-6 space-y-6 flex-1 overflow-y-auto">

        {/* Tabs: Meus Grupos | Descobrir */}
        <div className="flex gap-2 bg-slate-100 rounded-xl p-1">
          <button
            onClick={() => setActiveTab('meus')}
            className={`relative flex-1 py-2.5 px-4 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${activeTab === 'meus' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Shield className="w-4 h-4" />
            Meus Grupos
            {groups.length > 0 && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === 'meus' ? 'bg-blue-100 text-blue-600' : 'bg-slate-200 text-slate-500'}`}>
                {groups.length}
              </span>
            )}
            {meusNotificationCount > 0 && activeTab !== 'meus' && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {meusNotificationCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('descobrir')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${activeTab === 'descobrir' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Compass className="w-4 h-4" />
            Descobrir
            {discoveryCount > 0 && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === 'descobrir' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}>
                {discoveryCount}
              </span>
            )}
          </button>
        </div>

        {/* ─── Conteúdo da aba "Meus Grupos" ─── */}
        {activeTab === 'meus' && (<>

        {/* Busca dentro dos meus grupos */}
        {groups.length > 3 && (
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar nos meus grupos..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
            />
          </div>
        )}

        {/* Convites pendentes (entrada de grupo / próximo jogo) */}
        {invites.length > 0 && (
          <div>
            <h2 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
              <Bell className="w-4 h-4 text-blue-500" /> Convites pendentes
            </h2>
            <div className="space-y-2">
              {invites.map(invite => {
                const isGame = invite.scope === 'game';
                const formattedDate = isGame ? formatGameDate(invite.gameDate, invite.gameTime) : null;
                const recurrence = !isGame ? formatRecurrence(invite.dayOfWeek, invite.defaultTime) : null;
                const courtLine = [invite.courtName, invite.courtAddress].filter(Boolean).join(' · ');
                const locationLine = courtLine || invite.city || null;
                return (
                <div key={invite.id} className={`bg-white rounded-xl p-3 flex items-start justify-between gap-3 border-l-4 shadow-sm ${isGame ? 'border-l-blue-500 border border-blue-100' : 'border-l-emerald-500 border border-emerald-100'}`}>
                  <div className="min-w-0 space-y-0.5">
                    <div className="text-sm font-medium text-slate-700">{invite.groupName}</div>
                    <div className="text-xs text-slate-400">
                      {isGame ? 'Convite para o próximo jogo' : 'Convite para ser membro'} · {invite.fromUserName}
                    </div>
                    {invite.sport && (
                      <div className="text-[11px] text-slate-400">{invite.sport}</div>
                    )}
                    {formattedDate && (
                      <div className="text-[11px] text-slate-500 font-medium">{formattedDate}</div>
                    )}
                    {recurrence && (
                      <div className="text-[11px] text-slate-500 font-medium">{recurrence}</div>
                    )}
                    {locationLine && (
                      <div className="text-[11px] text-slate-400">{locationLine}</div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => respondInvite(invite.id, true)}
                      className="text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg"
                    >Aceitar</button>
                    <button
                      onClick={() => respondInvite(invite.id, false)}
                      className="text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-lg"
                    >Recusar</button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notificação de solicitações pendentes para admins */}
        {groups.some(g => g.ownerId === userId && g.pendingRequests?.length > 0) && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center gap-3">
            <Bell className="w-5 h-5 text-orange-500 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-orange-700">Solicitações pendentes</p>
              <p className="text-xs text-orange-500">
                {groups.filter(g => g.ownerId === userId && g.pendingRequests?.length > 0).map(g => `${g.name} (${g.pendingRequests.length})`).join(', ')}
              </p>
            </div>
          </div>
        )}

        {/* 3. Check-ins externos pendentes para admins */}
        {pendingExternalCheckinGames.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-amber-500 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-700">Check-ins externos aguardando</p>
                <p className="text-xs text-amber-500">{pendingExternalCheckinGames.length} jogo{pendingExternalCheckinGames.length !== 1 ? 's' : ''} com check-in pendente</p>
              </div>
            </div>
            <div className="space-y-1 pl-8">
              {pendingExternalCheckinGames.map(game => {
                const grp = groups.find(g => g.id === game.groupId);
                const pendingCount = (game.externalCheckins || []).filter(id => !(game.confirmedExternalCheckins || []).includes(id)).length;
                return (
                  <button key={game.id} onClick={() => navigate(`/games/${game.groupId}/${game.id}`)} className="w-full text-left text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1">
                    <ChevronRight className="w-3 h-3" />
                    {grp?.name} — {pendingCount} pendente{pendingCount !== 1 ? 's' : ''}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-4">

            {/* 4. Meus Grupos */}
            {groups.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h2 className="font-bold text-slate-800">Seus Grupos</h2>
                  <Button size="sm" variant="ghost" className="text-blue-600 bg-blue-50 hover:bg-blue-100" onClick={() => navigate('/groups/create')}><Plus className="w-4 h-4" /> Novo</Button>
                </div>
                {filteredGroups.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-sm">Nenhum grupo encontrado.</div>
                ) : (
                  <div className="lg:grid lg:grid-cols-2 lg:gap-3">
                  {filteredGroups.map(group => {
                    const nextGame = getActiveGame(group);
                    const isAdmin = group.ownerId === userId;
                    const pendingCount = isAdmin ? (group.pendingRequests?.length || 0) : 0;
                    return (
                      <div key={group.id} onClick={() => navigate(`/groups/${group.id}`)} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all relative overflow-hidden mb-3 lg:mb-0 cursor-pointer">
                        <div className="p-5">
                          {/* Linha 1: Nome + ADMIN + pendentes + seta */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <h3 className="text-lg font-bold text-slate-800 truncate">{group.name}</h3>
                              {isAdmin && <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0"><Shield className="w-3 h-3" /> ADMIN</span>}
                              {pendingCount > 0 && <span className="bg-orange-100 text-orange-600 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0"><Bell className="w-3 h-3" /> {pendingCount}</span>}
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
                          </div>

                          {/* Linha 2: Sport + privacidade + membros */}
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded uppercase tracking-wide">{group.sport}</span>
                            {group.isPrivate ? <span className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> Privado</span> : <span className="bg-emerald-50 text-emerald-600 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><Globe className="w-3 h-3" /> Publico</span>}
                            <span className="text-xs text-slate-400">{group.memberIds?.length || 1} membros</span>
                          </div>

                          {nextGame ? (
                            <>
                              {/* Data/hora */}
                              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1.5">
                                <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                                <span>{parseLocalDate(nextGame.date).toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })} - {nextGame.time}</span>
                                {nextGame.status === 'voting' && <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full flex items-center gap-1"><Vote className="w-3 h-3" /> Votando</span>}
                              </div>

                              {/* Localização */}
                              {(group.city || group.courtName) && (
                                <div className="flex items-center gap-2 text-xs text-slate-400 mb-1.5">
                                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                                  <span>{[group.city, group.courtName].filter(Boolean).join(' - ')}</span>
                                </div>
                              )}

                              {/* Organizador */}
                              <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                                <Shield className="w-3.5 h-3.5 shrink-0 text-blue-400" />
                                <span>Organizado por: {displayName(getUser(group.ownerId).name)}</span>
                              </div>

                              {/* Confirmados / Votação — barra de progresso */}
                              {(() => {
                                if (nextGame.status === 'voting') {
                                  const extIds = nextGame.confirmedExternalCheckins || [];
                                  const voted = Object.keys(nextGame.votes || {}).length;
                                  const total = (nextGame.confirmedIds || []).filter(pid => !pid.startsWith('guest_') && !extIds.includes(pid)).length;
                                  const remaining = total - voted;
                                  return (
                                    <>
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm font-bold text-indigo-600">{voted} de {total} votaram</span>
                                        {remaining > 0 && <span className="text-sm font-medium text-slate-400">faltam {remaining}</span>}
                                      </div>
                                      <div className="w-full h-2 bg-slate-100 rounded-full mb-3 overflow-hidden">
                                        <div className="h-full bg-indigo-500 rounded-full transition-all duration-300" style={{ width: `${total > 0 ? Math.min(100, (voted / total) * 100) : 0}%` }} />
                                      </div>
                                    </>
                                  );
                                }
                                const confirmed = nextGame.confirmedIds?.length || 0;
                                const max = nextGame.maxPlayers || 14;
                                const spots = Math.max(0, max - confirmed);
                                return (
                                  <>
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-sm font-bold text-slate-700">{confirmed} de {max} confirmados</span>
                                      {spots > 0 && <span className="text-sm font-medium text-amber-600">faltam {spots}</span>}
                                    </div>
                                    <div className="w-full h-2 bg-slate-100 rounded-full mb-3 overflow-hidden">
                                      <div className="h-full bg-emerald-500 rounded-full transition-all duration-300" style={{ width: `${Math.min(100, (confirmed / max) * 100)}%` }} />
                                    </div>
                                  </>
                                );
                              })()}

                              {/* Avatars + status confirmação */}
                              <div className="flex items-center justify-between">
                                <div className="flex -space-x-2">
                                  {nextGame.confirmedIds?.slice(0, 4).map((uid, i) => (<div key={i} className="w-7 h-7 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px]">{getUser(uid).photo}</div>))}
                                  {(nextGame.confirmedIds?.length || 0) > 4 && <div className="w-7 h-7 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] text-slate-500 font-bold">+{nextGame.confirmedIds.length - 4}</div>}
                                </div>
                                {nextGame.confirmedIds?.includes(userId) ? <span className="flex items-center gap-1 text-emerald-600 font-bold text-xs bg-emerald-50 px-2 py-1 rounded-lg"><CheckCircle className="w-3 h-3" /> Confirmado</span> : <span className="text-slate-400 text-xs font-medium">{nextGame.confirmedIds?.length || 0}/{nextGame.maxPlayers}</span>}
                              </div>
                            </>
                          ) : (
                            <>
                              {(group.city || group.courtName) && (
                                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                                  <span>{[group.city, group.courtName].filter(Boolean).join(' - ')}</span>
                                </div>
                              )}
                              <p className="text-slate-400 text-sm py-2 italic">Nenhum jogo agendado.</p>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                )}
              </div>
            )}

            {/* Checkins externos */}
            {externalCheckinGames.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-blue-500" />
                  <h2 className="font-bold text-slate-800">Seus Check-ins</h2>
                </div>
                <div className="lg:grid lg:grid-cols-2 lg:gap-3">
                {externalCheckinGames.map(game => {
                  const isConfirmed = (game.confirmedExternalCheckins || []).includes(userId);
                  return (
                  <div key={game.id} onClick={() => navigate(`/games/${game.groupId}/${game.id}`)} className={`bg-white rounded-2xl shadow-sm overflow-hidden mb-3 lg:mb-0 cursor-pointer hover:shadow-md transition-all border ${isConfirmed ? 'border-emerald-200' : 'border-blue-200'}`}>
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded uppercase tracking-wide mb-1">{game.group?.sport}</span>
                          <h3 className="text-base font-bold text-slate-800">{game.group?.name}</h3>
                        </div>
                        {isConfirmed
                          ? <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full shrink-0">Confirmado ✓</span>
                          : <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full shrink-0">Aguardando</span>
                        }
                      </div>
                      {game.group?.city && (
                        <p className="text-slate-400 text-xs flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" />{[game.group.city, game.group.courtName].filter(Boolean).join(' - ')}</p>
                      )}
                      <p className="text-slate-500 text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {parseLocalDate(game.date).toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })} - {game.time}
                      </p>
                    </div>
                  </div>
                  );
                })}
                </div>
              </div>
            )}

            {/* Empty state — sem grupos e sem busca */}
            {groups.length === 0 && !searchQuery && externalCheckinGames.length === 0 && (
              <div className="text-center py-16">
                <img src="/icone.png" alt="" className="w-24 h-24 mx-auto mb-4 opacity-70" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">Bora achar um fut?</h3>
                <p className="text-slate-400 text-sm mb-4 max-w-[240px] mx-auto">
                  Adiciona a tua cidade e encontra peladas perto de você.
                  {' '}Ou explora a aba <strong>Descobrir</strong> para encontrar peladeiros e grupos públicos por perto.
                </p>
                <button onClick={() => navigate('/groups/create')} className="inline-flex items-center gap-1 text-blue-600 text-sm font-medium hover:text-blue-700 bg-blue-50 px-4 py-2 rounded-full transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Criar meu grupo
                </button>
              </div>
            )}
        </div>
        </>)}

        {/* ─── Conteúdo da aba "Descobrir" ─── */}
        {activeTab === 'descobrir' && (
          <div className="space-y-4">

            {/* Banner de localização se perfil não tem cidade */}
            {!profile?.city && !overrideCity && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3">
                <Navigation className="w-5 h-5 text-blue-500 shrink-0" />
                <div className="flex-1 text-sm text-blue-700">
                  Adiciona a tua cidade no perfil para descobrir peladas perto de ti
                </div>
                <button
                  onClick={() => navigate('/profile')}
                  className="text-xs font-bold text-blue-600 bg-white border border-blue-200 px-2 py-1 rounded-lg hover:bg-blue-50 shrink-0"
                >
                  Perfil
                </button>
              </div>
            )}

            {/* Slider de raio + cidade (quando tem cidade) */}
            {(profile?.city || overrideCity) && (
              <div className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-emerald-500" />
                    <span className={`text-sm font-semibold ${editingCity ? 'text-slate-400' : 'text-slate-700'}`}>{activeCity}</span>
                    {!editingCity && overrideCity && (
                      <button onClick={() => { setOverrideCity(''); setPendingCity(''); setOverrideCoords(null); }} className="text-[10px] text-slate-400 hover:text-red-400 ml-1">✕</button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {editingCity ? (
                      <button onClick={() => setEditingCity(false)} className="text-xs text-slate-400 hover:text-slate-600">Cancelar</button>
                    ) : (
                      <button onClick={() => setEditingCity(true)} className="text-xs text-blue-500 font-medium hover:text-blue-700">Alterar</button>
                    )}
                    <span className="text-sm font-bold text-emerald-600">{radius} km</span>
                  </div>
                </div>
                <input
                  type="range"
                  min={5}
                  max={200}
                  step={5}
                  value={radius}
                  onChange={e => setRadius(Number(e.target.value))}
                  className={`w-full accent-emerald-500 ${editingCity ? 'mb-2' : ''}`}
                />
                <div className={`flex justify-between text-[10px] text-slate-400 ${editingCity ? 'mb-2' : 'mt-0.5'}`}>
                  <span>5 km</span>
                  <span>200 km</span>
                </div>
                {editingCity && (
                  <CityAutocomplete
                    value={pendingCity}
                    onChange={setPendingCity}
                    onSelect={(city) => {
                      setOverrideCity(city.label);
                      setPendingCity(city.label);
                      setEditingCity(false);
                    }}
                  />
                )}
              </div>
            )}

            {/* Busca por grupo/quadra dentro do raio */}
            {(profile?.city || overrideCity) && (
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar grupo ou quadra..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-emerald-400"
                />
              </div>
            )}

            {/* Banner de incentivo para disponibilidade (apareça para outros) */}
            {profile?.city && !hasAvailability && !hideBanner && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-3">
                <Clock className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-emerald-800">Apareça para outros peladeiros!</p>
                  <p className="text-xs text-emerald-600 mt-0.5">Adiciona a tua disponibilidade no perfil para aparecer na busca por jogadores.</p>
                  <div className="flex gap-3 mt-2">
                    <button onClick={() => navigate('/profile')} className="text-xs font-bold text-emerald-700 underline">Ir para o Perfil</button>
                    <button onClick={() => { localStorage.setItem('hideAvailBanner', 'true'); setHideBanner(true); }} className="text-xs text-emerald-500">Não exibir novamente</button>
                  </div>
                </div>
              </div>
            )}

            {/* Peladeiros disponíveis (topo, com filtro de raio) */}
            {availablePlayers.length > 0 ? (
              <div>
                <h2 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-500" /> Peladeiros disponíveis
                </h2>
                {activeCity && (
                  <p className="text-xs text-slate-400 mb-2">
                    Dentro de {radius} km de {activeCity.split(' -')[0]}
                  </p>
                )}
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {availablePlayers.map(u => {
                    const isMe = u.id === userId;
                    return (
                      <div
                        key={u.id}
                        onClick={() => navigate(`/players/${u.id}`)}
                        className={`flex-shrink-0 flex flex-col items-center bg-white rounded-xl p-3 cursor-pointer w-24 ${isMe ? 'border border-emerald-300 ring-1 ring-emerald-200' : 'border border-slate-200 hover:border-emerald-300'}`}
                      >
                        <PlayerAvatar user={u} size="sm" />
                        <span className="text-xs font-medium text-slate-700 mt-1 truncate w-full text-center">{u.name?.split(' ')[0]}</span>
                        <PositionBadge position={u.position} />
                        {isMe ? (
                          <span className="text-[10px] text-emerald-500 font-medium mt-1">Você</span>
                        ) : u.city ? (
                          <span className="text-[10px] text-slate-400 mt-1 text-center leading-tight w-full">{u.city}</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : activeCity ? (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <Users className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 font-medium">Nenhum peladeiro disponível por perto</p>
                <p className="text-xs text-slate-400 mt-1">Aumenta o raio ou tenta outra cidade.</p>
              </div>
            ) : null}

            {/* 5. Peladas (grupos públicos descobertos por raio/cidade) */}
            {filteredPublicGroups.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-4 h-4 text-slate-400" />
                  <h2 className="font-bold text-slate-800">Peladas</h2>
                </div>
                {filteredPublicGroups.map(group => {
                  const spotsInfo = getSpotsInfo(group);
                  const spotsColor = spotsInfo ? (spotsInfo.spots > 4 ? 'text-blue-600 bg-blue-50' : spotsInfo.spots > 0 ? 'text-orange-600 bg-orange-50' : 'text-red-600 bg-red-50') : '';
                  const borderColor = spotsInfo ? (spotsInfo.spots > 4 ? 'border-blue-200' : spotsInfo.spots > 0 ? 'border-orange-200' : 'border-red-200') : 'border-slate-200';
                  return (
                    <div key={group.id} className={`bg-white rounded-2xl border ${borderColor} shadow-sm overflow-hidden mb-3`}>
                      <div className="p-4">
                        <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded mb-2 uppercase tracking-wide">{group.sport}</span>
                        <h3 className="text-base font-bold text-slate-800 mb-1">{group.name}</h3>
                        {(group.city || group.courtName) && (
                          <p className="text-slate-400 text-xs flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" />{[group.city, group.courtName].filter(Boolean).join(' - ')}</p>
                        )}
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-slate-400 text-sm">{group.memberIds?.length || 1} membro{(group.memberIds?.length || 1) !== 1 ? 's' : ''}</span>
                          {spotsInfo && (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${spotsColor}`}>
                              {spotsInfo.spots > 0 ? `${spotsInfo.spots} vaga${spotsInfo.spots !== 1 ? 's' : ''}` : 'Lotado'}
                            </span>
                          )}
                        </div>
                        {spotsInfo && (
                          <p className="text-slate-400 text-xs mb-3 flex items-center gap-2">
                            <Calendar className="w-3 h-3" />
                            {parseLocalDate(spotsInfo.game.date).toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })} - {spotsInfo.game.time}
                          </p>
                        )}
                        {group.isPrivate ? (
                          <Button size="sm" className="w-full" variant="secondary" disabled>
                            <Lock className="w-4 h-4" /> Grupo Privado
                          </Button>
                        ) : group.pendingRequests?.some(r => r.userId === userId) ? (
                          <Button size="sm" className="w-full" variant="secondary" disabled>
                            <CheckCircle className="w-4 h-4" /> Solicitacao Enviada
                          </Button>
                        ) : (
                          <Button size="sm" className="w-full" onClick={() => requestToJoin(group.id)}>
                            <LogIn className="w-4 h-4" /> Solicitar Entrada
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Vagas Abertas */}
            {openSpotGames.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <h2 className="font-bold text-slate-800">Vagas Abertas</h2>
                </div>
                {openSpotGames.map(game => (
                  <div key={game.id} className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden mb-3">
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded uppercase tracking-wide mb-1">{game.group?.sport}</span>
                          <h3 className="text-base font-bold text-slate-800">{game.group?.name}</h3>
                        </div>
                        <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full shrink-0">{game.spotsLeft} vaga{game.spotsLeft !== 1 ? 's' : ''}</span>
                      </div>
                      {game.group?.city && (
                        <p className="text-slate-400 text-xs flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" />{[game.group.city, game.group.courtName].filter(Boolean).join(' - ')}</p>
                      )}
                      <p className="text-slate-500 text-sm flex items-center gap-2 mb-3">
                        <Calendar className="w-4 h-4" />
                        {parseLocalDate(game.date).toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })} - {game.time}
                      </p>
                      {game.openSpotsPositions?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          <span className="text-xs text-slate-400 font-medium">Falta:</span>
                          {game.openSpotsPositions.map((pos, pi) => (
                            <PositionBadge key={pi} position={pos} />
                          ))}
                        </div>
                      )}
                      <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-600" onClick={(e) => { e.stopPropagation(); checkinOpenSpot(game.groupId, game.id); }}>
                        Quero Jogar!
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state — descobrir sem resultados */}
            {!hasDiscovery && (
              <div className="text-center py-10">
                <div className="text-4xl mb-3">🔍</div>
                <h3 className="text-lg font-bold text-slate-600 mb-2">Nada por perto ainda</h3>
                <p className="text-slate-400 text-sm mb-4 max-w-[280px] mx-auto">
                  {activeCity
                    ? `Não encontramos peladeiros, grupos ou vagas em ${activeCity.split(' -')[0]} dentro de ${radius} km. Tenta aumentar o raio.`
                    : 'Adiciona a tua cidade para descobrir peladas e jogadores por perto.'}
                </p>
                {!activeCity && (
                  <button onClick={() => navigate('/profile')} className="inline-flex items-center gap-1 text-blue-600 text-sm font-medium hover:text-blue-700 bg-blue-50 px-4 py-2 rounded-full transition-colors">
                    <Navigation className="w-3.5 h-3.5" /> Definir cidade
                  </button>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </>
  );
};

export default HomePage;
